// hooks/useIntercom.ts
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useRecoilValueLoadable } from 'recoil';

import { IUserAtom, user } from '../../recoil/atoms/user';
import { CRMDisplayNameMap, CRMNameMap } from '../constants/CRMNameMap';

type UserExport = {
  email: string;
  name: string;
  phone: string;
  user_id: number;
  phone_country: string;
  hide_default_launcher?: boolean;
  userid: number;
  'User Type': string;
  'User Plan Tier': string;
  'Business Active': 'Yes' | 'No';
  CRM: string;
  crmName: string;
  callerid: string;
  businessid: number | undefined;
  'Reserve Balance': number;
  'Card Declined': string;
  'Paid Status': string;
  'Bill Day': number;
  'Bill Cycle': string;
  'Contract Terms': string;
  'Account Manager Email': string;
  'Sales Rep': string;
  'Setup Rep': string;
  'Deal Closed Date': number; // Unix timestamp
  countrycode: string;
  businessBillingTerm: number;
};
export function useIntercom() {
  const userAtom = useRecoilValueLoadable(user);
  const location = useLocation();
  const hasBooted = useRef(false);
  const [isBooted, setIsBooted] = useState(false);

  const getConfig = useCallback(
    (hideLauncher = false) => {
      const u = userAtom.contents?.user ?? {};
      const b = userAtom.contents?.business ?? {};
      const bill = userAtom.contents?.billingStatus ?? {};
      let unixTimestamp = 0;
      if (b?.dealClosedDateTime && b?.dealClosedDateTime !== null) {
        const date = new Date(b.dealClosedDateTime);
        unixTimestamp = Math.floor(date.getTime() / 1000);
      }
      const cfg: UserExport = {
        email: u.email,
        name: `${u?.fname ?? ''} ${u?.lname ?? ''}`.trim(),
        phone: u.smsDID,
        user_id: u.userid,
        phone_country: u.countrycode,
        userid: u.userid,
        'User Type': u.role,
        'User Plan Tier': u.tier,
        'Business Active': Number(b.isactive) === 1 ? 'Yes' : 'No',
        CRM: CRMDisplayNameMap[CRMNameMap[Number(u.crmid)]],
        crmName: CRMDisplayNameMap[CRMNameMap[Number(u.crmid)]],
        callerid: u.calleridName,
        businessid: b.businessid ?? userAtom.contents?.businessid,
        'Reserve Balance': b.reserveBalance,
        'Card Declined': Number(bill.cardDeclined) === 1 ? 'Yes' : 'No',
        'Paid Status': Number(b.prepaidTrial) === 1 ? 'Trial' : 'Paid',
        'Bill Day': b.billDay,
        'Bill Cycle':
          String(b.businessBillingTerm) +
          ' month billing ' +
          String(b.businessContractTerm) +
          ' month contract',
        businessBillingTerm: Number(b.businessBillingTerm),
        'Contract Terms': b.businessContractTerm,
        'Account Manager Email': b.accountManagerEmail,
        'Sales Rep': b.salesRepEmail,
        'Setup Rep': b.setupRepEmail,
        'Deal Closed Date': unixTimestamp,
        countrycode: u.countrycode,
      };
      if (hideLauncher) cfg.hide_default_launcher = true;
      return cfg;
    },
    [userAtom.contents]
  );

  useEffect(() => {
    if (
      hasBooted.current ||
      userAtom.state !== 'hasValue' ||
      !(userAtom.contents as IUserAtom)?.user?.userid
    )
      return;

    // Don't initialize Intercom on the AIOOutcome or Omni Reporting pages
    if (window.location.pathname === '/aio/outcome' || window.location.pathname === '/insights') {
      // If Intercom is already loaded, hide it
      if (window.Intercom) {
        window.Intercom?.('update', { hide_default_launcher: true });
      }
      return;
    }

    window.Intercom?.('boot', getConfig(true));

    setTimeout(() => {
      hasBooted.current = true;
      setIsBooted(true);
    }, 500);
  }, [getConfig, userAtom.state, userAtom.contents]);

  // Hide Intercom on specific pages
  useEffect(() => {
    if (!isBooted || !window.Intercom) return;

    if (window.location.pathname === '/aio/outcome' || window.location.pathname === '/insights') {
      // Hide Intercom on Omni Reporting page
      window.Intercom?.('update', { hide_default_launcher: true });
    } else {
      window.Intercom?.('update', {
        alignment: 'right',
        horizontal_padding: 20,
        vertical_padding: 20,
      });
    }
  }, [location.pathname, isBooted]);

  const openNewMessage = () => {
    window.Intercom?.('update', getConfig(true));
    window.Intercom?.('showNewMessage');
  };

  return { openNewMessage, isBooted };
}
